import { useEffect, useMemo, useRef, useState } from "react";
import { X, Download, Maximize2, Minimize2, List, ChevronRight, ExternalLink } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

/**
 * PdfFlipbook — premium fullscreen PDF viewer backed by the browser's native
 * PDF renderer via <iframe>. Compatible with React 19 (no external viewer lib).
 *
 * Props:
 *   doc: { title, pdf_url, file_size, page_count, filename_original }
 *   toc: [{ level, title, page (1-indexed within THIS view's page space) }, ...]
 *   startPage: optional 1-indexed page to open on load
 *   onClose
 *
 * ToC quick-jump works by remounting the iframe with an updated `#page=N`
 * URL fragment — this is a PDF viewer standard supported by every major
 * desktop and mobile browser (Chrome, Safari, Firefox, Edge).
 */
export default function PdfFlipbook({ doc, toc = [], startPage = null, onClose }) {
  const [isFull, setIsFull] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => Math.max(1, startPage || 1));
  const iframeRef = useRef(null);

  // Build the fully-qualified PDF URL with `#page=N&toolbar=1&view=FitH` params.
  // Query params first, hash last.
  const pdfSrc = useMemo(() => {
    if (!doc?.pdf_url) return "";
    const base = `${BACKEND_URL}${doc.pdf_url}`;
    const hash = `#page=${currentPage}&toolbar=1&navpanes=0&view=FitH`;
    return `${base}${hash}`;
  }, [doc?.pdf_url, currentPage]);

  const downloadHref = useMemo(() => {
    if (!doc?.pdf_url) return "";
    const base = `${BACKEND_URL}${doc.pdf_url}`;
    return `${base}${base.includes("?") ? "&" : "?"}download=1`;
  }, [doc?.pdf_url]);

  // Body scroll lock while the modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC key closes the modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!doc) return null;

  const openInNewTab = () => {
    if (!doc?.pdf_url) return;
    window.open(`${BACKEND_URL}${doc.pdf_url}#page=${currentPage}`, "_blank", "noopener,noreferrer");
  };

  const jump = (page1) => {
    const n = Math.max(1, Math.min(doc.page_count || 999, page1 || 1));
    setCurrentPage(n);
    setTocOpen(false);
  };

  return (
    <div
      data-testid="pdf-flipbook-modal"
      className="fixed inset-0 z-[60] bg-emerald-950/95 backdrop-blur-md flex flex-col animate-in fade-in duration-200"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 sm:px-5 py-3 border-b border-white/10 shrink-0 gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.24em] uppercase font-[Outfit] font-bold text-fuchsia-300">
            Program Library · Page {currentPage}
          </div>
          <div className="font-[Cormorant_Garamond] text-base sm:text-2xl text-white leading-tight truncate">
            {doc.title}
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {toc.length > 0 && (
            <button
              onClick={() => setTocOpen((v) => !v)}
              data-testid="pdf-modal-toc"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 h-9 text-xs font-[Outfit] font-bold transition-colors ${
                tocOpen ? "bg-fuchsia-600 text-white" : "bg-white/10 hover:bg-white/20 text-white"
              }`}
            >
              <List size={14} /> <span className="hidden sm:inline">Table of Contents</span>
              <span className="sm:hidden">ToC</span>
            </button>
          )}
          <a
            href={downloadHref}
            download={doc.filename_original || "program.pdf"}
            data-testid="pdf-modal-download"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 h-9 bg-white/10 hover:bg-white/20 text-white text-xs font-[Outfit] font-semibold"
          >
            <Download size={14} /> Download
          </a>
          <button
            onClick={openInNewTab}
            data-testid="pdf-modal-newtab"
            className="hidden sm:inline-flex rounded-full h-9 w-9 items-center justify-center bg-white/10 hover:bg-white/20 text-white"
            title="Open in new tab"
          >
            <ExternalLink size={15} />
          </button>
          <button
            onClick={() => setIsFull((v) => !v)}
            data-testid="pdf-modal-fullscreen"
            className="hidden sm:inline-flex rounded-full h-9 w-9 items-center justify-center bg-white/10 hover:bg-white/20 text-white"
            title={isFull ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFull ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button
            onClick={onClose}
            data-testid="pdf-modal-close"
            className="rounded-full h-9 w-9 flex items-center justify-center bg-fuchsia-600 hover:bg-fuchsia-700 text-white shadow-lg shadow-fuchsia-600/40"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Body — ToC drawer + iframe */}
      <div className="flex-1 min-h-0 flex bg-slate-100 relative">
        {/* ToC panel */}
        {toc.length > 0 && (
          <>
            {/* Mobile backdrop */}
            {tocOpen && (
              <button
                aria-hidden
                onClick={() => setTocOpen(false)}
                className="sm:hidden absolute inset-0 z-10 bg-emerald-950/40 animate-in fade-in duration-150"
              />
            )}
            <aside
              data-testid="pdf-toc-panel"
              className={`${
                tocOpen ? "translate-x-0" : "-translate-x-full sm:translate-x-0"
              } absolute sm:relative z-20 sm:z-auto top-0 bottom-0 left-0 w-[280px] sm:w-[300px] bg-white border-r border-emerald-950/10 transition-transform duration-200 ${
                !tocOpen ? "sm:w-0 sm:opacity-0 sm:overflow-hidden" : "sm:opacity-100"
              } flex flex-col`}
            >
              <div className="px-4 py-3 border-b border-emerald-100 flex items-center gap-2">
                <List size={14} className="text-fuchsia-700" />
                <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-800">Jump to section</div>
              </div>
              <ul className="flex-1 overflow-y-auto py-2">
                {toc.map((e, i) => {
                  const active = e.page === currentPage;
                  return (
                    <li key={`${e.title}-${e.page}-${i}`}>
                      <button
                        onClick={() => jump(e.page)}
                        data-testid={`pdf-toc-entry-${i}`}
                        className={`w-full text-left px-4 py-2.5 flex items-center gap-2 text-sm font-[Outfit] transition-colors border-l-2 ${
                          active
                            ? "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-500"
                            : "text-emerald-950 hover:bg-fuchsia-50 hover:text-fuchsia-800 border-transparent hover:border-fuchsia-500"
                        }`}
                      >
                        <span className={`text-[10px] font-mono shrink-0 min-w-[24px] ${active ? "text-fuchsia-700 font-bold" : "text-emerald-950/40"}`}>p{e.page}</span>
                        <span className="flex-1 truncate">{e.title}</span>
                        <ChevronRight size={14} className={active ? "text-fuchsia-500 shrink-0" : "text-emerald-950/30 shrink-0"} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>
          </>
        )}

        {/* Viewer — native browser PDF renderer */}
        <div className="flex-1 min-w-0 relative bg-slate-200">
          <iframe
            ref={iframeRef}
            key={pdfSrc /* remount on page jump so #page=N is respected */}
            src={pdfSrc}
            title={doc.title}
            data-testid="pdf-iframe"
            className="w-full h-full border-0"
            allow="fullscreen"
          />
        </div>
      </div>

      {/* Bottom bar — mobile actions (iOS often can't render PDF in iframe;
          give attendees a rock-solid "Open PDF" fallback) */}
      <div className="sm:hidden px-4 py-2 border-t border-white/10 flex items-center gap-2 shrink-0">
        <button
          onClick={openInNewTab}
          data-testid="pdf-modal-newtab-mobile"
          className="flex-1 rounded-full h-11 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-[Outfit] font-bold flex items-center justify-center gap-2"
        >
          <ExternalLink size={16} /> Open
        </button>
        <a
          href={downloadHref}
          download={doc.filename_original || "program.pdf"}
          data-testid="pdf-modal-download-mobile"
          className="flex-1 rounded-full h-11 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-[Outfit] font-bold flex items-center justify-center gap-2"
        >
          <Download size={16} /> {doc.file_size ? `${(doc.file_size / (1024 * 1024)).toFixed(1)}MB` : "PDF"}
        </a>
      </div>
    </div>
  );
}
